import {
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    ControlValueAccessor,
    FormControl,
    FormControlStatus,
    NgControl,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { NgClass } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ValidationMessagePipe } from '@ui/pipes/validation-message/validation-message.pipe';


@Component({
    selector: 'ui-form-input',
    templateUrl: './form-input.component.html',
    styleUrls: ['./form-input.component.scss'],
    encapsulation: ViewEncapsulation.None,
    imports: [ReactiveFormsModule, NgClass, ValidationMessagePipe],
    host: {
        '[class.hidden-hint]': 'hideHint',
        class: 'ui-form-input',
    },
})
export class FormInputComponent implements ControlValueAccessor, OnInit, OnDestroy {
    @Input()
    public label: string = '';

    @Input()
    public hint: string = '';

    @Input()
    public placeholder: string = '';

    @Input()
    public type: string = 'text';

    @Input()
    public value: string;

    @Input()
    public additionalGroupClass: string;

    @Input()
    public additionalLabelClass: string;

    @Input()
    public maxLength: number;

    @Input()
    public disabled: boolean;

    @Input()
    public readonly: boolean = false;

    @Input()
    public hideHint?: boolean = false;

    @Input()
    public preValidate?: boolean = false;

    @Input()
    public showErrorMessage?: boolean = true;

    @Input()
    public size?: 'sm' | 'lg' = null;

    @Input()
    public id: string = 'ui-input-' + Math.random().toString().replace('.', '');

    @Input()
    public ariaDescribedby: string;

    @Output()
    public enterPressed: EventEmitter<Event> = new EventEmitter<Event>();

    @Output()
    public suffixClick: EventEmitter<MouseEvent> = new EventEmitter<MouseEvent>();

    @ContentChild('suffix')
    public suffixElement: ElementRef;

    @ViewChild('input') public inputElement: ElementRef<HTMLInputElement>;

    public inputControl: FormControl;

    public hasBlur: boolean;

    private readonly ngControl: NgControl = inject(NgControl, { self: true, optional: true });
    private readonly destroy$: Subject<unknown> = new Subject<unknown>();
    readonly #cd: ChangeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        if (this.ngControl) {
            this.ngControl.valueAccessor = this;
        }
    }

    public get isRequiredField(): boolean {
        return this.inputControl.hasValidator(Validators.required);
    }

    public ngOnInit(): void {
        this.inputControl = this.ngControl?.control as FormControl;
        this.disabled = this.inputControl.status === 'DISABLED';

        this.inputControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe((status: FormControlStatus) => {
            this.disabled = status === 'DISABLED';
        });

        if (this.preValidate) {
            this.inputControl.markAsDirty();
        }
    }

    public ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }

    public onChange: (value: string) => void;
    public onTouch: () => void;

    public registerOnChange(fn: (v: string) => void): void {
        this.onChange = fn;
    }

    public registerOnTouched(fn: () => void): void {
        this.onTouch = fn;
    }

    public setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    public writeValue(value: string): void {
        this.value = value;

        if (this.inputElement) {
            this.inputElement.nativeElement.value = value ?? '';
        }

        this.#cd.markForCheck();
    }

    public handlePressEnter(event: Event): void {
        this.enterPressed.emit(event);
    }

    public handlerKeyUp(event: KeyboardEvent): void {
        // @ts-ignore
        this.onChange(this.prepareValue(event?.target?.value));
        this.onTouch();
    }

    public handlerChange(event: Event) {
        //@ts-ignore
        this.onChange(this.prepareValue(event?.target?.value));
        this.onTouch();
    }

    public handleSuffixClick(event: MouseEvent): void {
        event.stopPropagation();
        this.suffixClick.emit(event);
    }

    private prepareValue(value: string): string {
        if (typeof value === 'string' && this.type === 'text' && this.maxLength) {
            return value.substring(0, this.maxLength);
        } else {
            return value;
        }
    }
}
